/**
 * Debounced autosave.
 *
 * The editor never shows a Save button — plans are saved continuously. Three
 * things make that safe:
 *   1. one save in flight at a time, with a re-check afterwards, so rapid edits
 *      coalesce instead of racing;
 *   2. whole-document writes, so a retry can never half-apply;
 *   3. optimistic version checks, so a second tab is detected rather than
 *      silently overwriting this one.
 */

import { useCallback, useEffect, useRef } from 'react'

import { api } from '../lib/api'
import { messageForError } from '../lib/i18n/errors'
import { useT } from '../lib/i18n/I18nProvider'
import { docOf } from '../lib/merge'
import { pullAndIntegrate } from '../lib/sync'
import { ApiError } from '../lib/types'
import { usePlanStore } from '../store/planStore'
import { useViewStore } from '../store/viewStore'

const DEBOUNCE_MS = 900
/** Losing the race five times running means something else is wrong. */
const MAX_CONFLICT_REBASES = 5

export function useAutosave(enabled: boolean): { saveNow: () => Promise<void> } {
  const inFlight = useRef(false)
  const pending = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const conflictRetries = useRef(0)
  // Error prose is user-facing, so it comes from the catalogue — read through
  // a ref so `flush` stays referentially stable across language switches.
  const t = useT()
  const tRef = useRef(t)
  tRef.current = t

  const flush = useCallback(async () => {
    const store = usePlanStore.getState()
    const plan = store.plan
    if (!plan) return
    if (store.saveStatus === 'conflict') return

    if (inFlight.current) {
      pending.current = true
      return
    }

    inFlight.current = true
    store.setSaveStatus('saving')
    try {
      // Fold the live camera in at save time — it is not part of the document.
      const camera = useViewStore.getState()
      const payload = {
        ...plan,
        viewport: { x: camera.x, y: camera.y, zoom: camera.zoom },
      }
      // Exactly what we send becomes the merge base once storage takes it.
      const sentDoc = docOf(payload)
      const saved = await api.saveLayout(payload)
      const after = usePlanStore.getState()
      // An edit made while the request was in flight set the status to
      // 'dirty'; note it BEFORE folding the response in, or the label below
      // would claim "saved" while unsaved work exists — and the flush when the
      // tab is hidden only fires for 'dirty', so the lie would cost data.
      const editedDuringFlight = after.saveStatus === 'dirty'
      after.applySavedPlan(saved, sentDoc)
      conflictRetries.current = 0
      // Only claim "saved" if nothing changed while the request was in flight.
      after.setSaveStatus(pending.current || editedDuringFlight ? 'dirty' : 'saved')
    } catch (error) {
      const store2 = usePlanStore.getState()
      if (error instanceof ApiError && error.isConflict) {
        // Another tab saved first. That is a rebase, not a dead end: read what
        // it wrote, merge our work into it, and let the ordinary
        // debounced save carry the result — `integrateStoredPlan` leaves the
        // store dirty exactly when something of ours still needs saving, and
        // the dirt subscription below schedules the next flush by itself.
        conflictRetries.current += 1
        if (conflictRetries.current > MAX_CONFLICT_REBASES) {
          store2.setSaveStatus('conflict', tRef.current.errors.conflict)
        } else if (!(await pullAndIntegrate(plan.id))) {
          store2.setSaveStatus('error', tRef.current.errors.unknown)
        }
      } else {
        // Never surface an error's English prose — branch on the stable code
        // and render from the catalogue.
        store2.setSaveStatus('error', messageForError(tRef.current, error))
      }
    } finally {
      inFlight.current = false
      if (pending.current) {
        pending.current = false
        void flush()
      }
    }
  }, [])

  // Watch for dirt and schedule a save.
  useEffect(() => {
    if (!enabled) return undefined
    return usePlanStore.subscribe((state, previous) => {
      if (state.saveStatus !== 'dirty') return
      if (state.plan === previous.plan && state.saveStatus === previous.saveStatus) return
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => void flush(), DEBOUNCE_MS)
    })
  }, [enabled, flush])

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  // Save at once when the tab goes away rather than waiting out the debounce: a
  // backgrounded tab on a phone may be frozen or killed before the timer fires.
  // `pagehide` covers iOS, which does not always send `visibilitychange`.
  useEffect(() => {
    if (!enabled) return undefined
    const saveIfDirty = () => {
      if (usePlanStore.getState().saveStatus !== 'dirty') return
      if (timer.current) clearTimeout(timer.current)
      void flush()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') saveIfDirty()
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', saveIfDirty)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', saveIfDirty)
    }
  }, [enabled, flush])

  return { saveNow: flush }
}

/**
 * Resolves once nothing is waiting to be saved — immediately if nothing is.
 * Something that reads *stored* data on the planner's behalf (a backup) calls
 * this first, so it never hands out a copy older than the canvas on screen.
 * Gives up after `timeoutMs` rather than hanging on a save that keeps failing.
 */
export function whenSaved(timeoutMs = 5000): Promise<void> {
  const pending = (status: string) => status === 'dirty' || status === 'saving'
  return new Promise((resolve) => {
    if (!pending(usePlanStore.getState().saveStatus)) {
      resolve()
      return
    }
    const finish = () => {
      clearTimeout(timer)
      unsubscribe()
      resolve()
    }
    const timer = setTimeout(finish, timeoutMs)
    const unsubscribe = usePlanStore.subscribe((state) => {
      if (!pending(state.saveStatus)) finish()
    })
  })
}
